import WorkerCtor from './mlsWorker.js?worker';
import workerModuleAssetUrl from './mlsWorker.js?worker&url';
import { ensureMlsStorageSchema } from './mlsStorageSchema.js';

let workerSource = 'uninitialized';
let workerSourceUrl = null;

const buildWorkerCandidates = () => {
  const candidates = [];

  const pushModuleUrlCandidate = (id, url) => {
    if (!url || typeof url !== 'string') {
      return;
    }
    const trimmed = url.trim();
    if (!trimmed || trimmed.startsWith('import:')) {
      return;
    }

    let normalizedHref = trimmed;
    const baseCandidates = [];
    if (typeof window !== 'undefined' && window.location?.href) {
      baseCandidates.push(window.location.href);
    } else if (typeof self !== 'undefined' && self.location?.href) {
      baseCandidates.push(self.location.href);
    }
    baseCandidates.push('http://localhost/');

    for (const base of baseCandidates) {
      try {
        const normalized = new URL(trimmed, base);
        normalizedHref = normalized.href;
        break;
      } catch {}
    }

    candidates.push({
      id,
      url: normalizedHref,
      instantiate: (options) => new Worker(normalizedHref, { type: 'module', ...options }),
    });
  };

  if (typeof WorkerCtor === 'function') {
    const resolvedUrl = typeof WorkerCtor?.url === 'string' ? WorkerCtor.url : null;
    const isViteImportPlaceholder = !resolvedUrl || resolvedUrl.startsWith('import:');

    if (!isViteImportPlaceholder) {
      pushModuleUrlCandidate('module-url', resolvedUrl);
    }

    candidates.push({
      id: 'vite-import',
      instantiate: (options) => new WorkerCtor(options),
      url: resolvedUrl || 'import:./mlsWorker.js?worker',
    });

    if (isViteImportPlaceholder) {
      const assetUrl = typeof workerModuleAssetUrl === 'string' ? workerModuleAssetUrl : './mlsWorker.js';
      pushModuleUrlCandidate('module-local', assetUrl);
    }
  } else {
    const assetUrl = typeof workerModuleAssetUrl === 'string' ? workerModuleAssetUrl : './mlsWorker.js';
    pushModuleUrlCandidate('module-local', assetUrl);
  }

  return candidates;
};

function attemptWorkerInstantiation(candidates, startIndex = 0, options = {}) {
  let lastError = null;

  for (let index = startIndex; index < candidates.length; index += 1) {
    const candidate = candidates[index];

    try {
      const instance =
        typeof candidate.instantiate === 'function'
          ? candidate.instantiate(options)
          : new Worker(candidate.url, { type: 'module', ...options });
      workerSource = candidate.id;
      workerSourceUrl = candidate.url != null ? String(candidate.url) : null;
      if (typeof console?.debug === 'function') {
        try {
          console.debug('[MLS] worker instantiated', {
            strategy: candidate.id,
            url: workerSourceUrl,
          });
        } catch {}
      }
      return { instance, lastError: null };
    } catch (error) {
      lastError = error;
      if (typeof console?.debug === 'function') {
        try {
          console.debug('[MLS] worker instantiation failed', {
            strategy: candidate.id,
            url: String(candidate.url),
            error,
          });
        } catch {}
      }
    }
  }

  return { instance: null, lastError };
}

function createWorkerInstance(options = {}) {
  if (typeof Worker === 'undefined') {
    return null;
  }

  const candidates = buildWorkerCandidates();
  const { instance, lastError } = attemptWorkerInstantiation(candidates, 0, options);

  if (!instance && lastError && typeof console?.warn === 'function') {
    try {
      console.warn('[MLS] exhausted MLS worker instantiation strategies, using stub', lastError);
    } catch {}
  }

  return instance;
}

function instantiateNextWorkerAfter(failedCandidateId, options = {}) {
  if (typeof Worker === 'undefined') {
    return { instance: null, lastError: null };
  }

  const candidates = buildWorkerCandidates();
  const currentIndex = candidates.findIndex((candidate) => candidate.id === failedCandidateId);
  const startIndex = currentIndex >= 0 ? currentIndex + 1 : 0;

  return attemptWorkerInstantiation(candidates, startIndex, options);
}

class StubWorker {
  constructor() {
    this.listeners = new Map();
  }

  addEventListener(type, listener) {
    if (!type || typeof listener !== 'function') return;
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(listener);
  }

  removeEventListener(type, listener) {
    if (!type || typeof listener !== 'function') return;
    const bucket = this.listeners.get(type);
    if (bucket) {
      bucket.delete(listener);
      if (bucket.size === 0) {
        this.listeners.delete(type);
      }
    }
  }

  postMessage() {
    throw new Error('MLS worker is unavailable in this environment');
  }

  terminate() {
    this.listeners.clear();
  }
}

let worker = null;
let nextId = 0;
const pending = new Map();
let storageReadyPromise = null;
const DEFAULT_WORKER_CALL_TIMEOUT_MS = 15000;
const INIT_WORKER_CALL_TIMEOUT_MS = 60000;

const ensureStorageReady = () => {
  if (!storageReadyPromise) {
    storageReadyPromise = ensureMlsStorageSchema().catch((error) => {
      storageReadyPromise = null;
      if (typeof console?.warn === 'function') {
        try {
          console.warn('[MLS] Failed ensuring storage schema, continuing with best effort', error);
        } catch {}
      }
      return null;
    });
  }
  return storageReadyPromise;
};

function createWorkerRestartedError() {
  const error = new Error('mls_worker_restarted');
  error.code = 'MLS_WORKER_RESTARTED';
  return error;
}

function createWorkerFallbackFailedError() {
  const error = new Error('mls_worker_fallback_failed');
  error.code = 'MLS_WORKER_FALLBACK_FAILED';
  return error;
}

function clearPendingEntryTimeout(entry) {
  if (!entry || !entry.timeoutId) return;
  try {
    clearTimeout(entry.timeoutId);
  } catch {}
  entry.timeoutId = null;
}

function rejectAllPending(error) {
  if (pending.size === 0) return;
  const entries = Array.from(pending.values());
  pending.clear();
  entries.forEach((entry) => {
    clearPendingEntryTimeout(entry);
    const reject = entry?.reject;
    try {
      reject(error);
    } catch {}
  });
}

function handleWorkerMessage(event) {
  if (event?.data?.type === 'MLS_WORKER_ERROR') {
    console.error('[MLS main] worker reported error', {
      error: event.data.err,
      source: workerSource,
      url: workerSourceUrl,
    });
    return;
  }

  const { id, status, result, error } = event?.data || {};
  if (!id) return;
  const entry = pending.get(id);
  if (!entry) return;

  pending.delete(id);
  clearPendingEntryTimeout(entry);
  if (status === 'ok') {
    entry.resolve(result);
    return;
  }

  let message = 'mls_worker_error';
  let metadata = {};
  if (error && typeof error === 'object') {
    metadata = { ...error };
    if (typeof error.message === 'string' && error.message) {
      message = error.message;
    } else if (typeof error.code === 'string' && error.code) {
      message = error.code;
    }
  } else if (typeof error === 'string' && error) {
    message = error;
  }

  const workerError = new Error(message);
  Object.entries(metadata).forEach(([key, value]) => {
    if (key === 'message') return;
    workerError[key] = value;
  });
  entry.reject(workerError);
}

function handleWorkerMessageError(event) {
  console.error('[MLS main] worker.onmessageerror', {
    event,
    source: workerSource,
    url: workerSourceUrl,
  });
}

function attachWorkerEventListeners(instance) {
  if (!instance) return;
  if (typeof instance.addEventListener === 'function') {
    instance.addEventListener('message', handleWorkerMessage);
  }
  if ('onerror' in instance || typeof instance.onerror === 'undefined') {
    instance.onerror = handleWorkerError;
  }
  if ('onmessageerror' in instance || typeof instance.onmessageerror === 'undefined') {
    instance.onmessageerror = handleWorkerMessageError;
  }
}

function handleWorkerError(event) {
  const details = {
    message: event?.message || null,
    filename: event?.filename || null,
    lineno: event?.lineno || null,
    colno: event?.colno || null,
    source: workerSource,
    url: workerSourceUrl,
  };
  if (event?.error) {
    details.error = event.error;
    details.stack = event.error?.stack;
  }
  console.error('[MLS main] worker.onerror', details);
  try {
    console.warn('[MLS] worker restarting after error', {
      source: workerSource,
      url: workerSourceUrl,
      message: details.message,
    });
  } catch {}

  const previousWorker = worker;
  worker = null;
  if (previousWorker && typeof previousWorker.terminate === 'function') {
    try {
      previousWorker.terminate();
    } catch {}
  }

  const isViteImportPlaceholder =
    workerSource === 'vite-import' && typeof workerSourceUrl === 'string' && workerSourceUrl.startsWith('import:');
  if (!isViteImportPlaceholder) {
    workerSource = 'stub';
    workerSourceUrl = null;
    worker = new StubWorker();
    rejectAllPending(createWorkerFallbackFailedError());
    return;
  }

  const { instance, lastError } = instantiateNextWorkerAfter('vite-import');
  if (instance) {
    worker = instance;
    attachWorkerEventListeners(worker);
    rejectAllPending(createWorkerRestartedError());
    return;
  }

  if (lastError && typeof console?.warn === 'function') {
    try {
      console.warn('[MLS] exhausted MLS worker fallback strategies, using stub', lastError);
    } catch {}
  }

  workerSource = 'stub';
  workerSourceUrl = null;
  worker = new StubWorker();
  rejectAllPending(createWorkerFallbackFailedError());
}

function ensureWorker() {
  if (worker) return worker;

  const instance = createWorkerInstance();
  if (instance) {
    worker = instance;
    attachWorkerEventListeners(worker);
    if (typeof console?.info === 'function') {
      try {
        console.info('[MLS] worker started', {
          source: workerSource,
          url: workerSourceUrl,
        });
      } catch {}
    }
  } else {
    workerSource = 'stub';
    workerSourceUrl = null;
    worker = new StubWorker();
  }

  return worker;
}

export function callMlsWorker(command, payload, options = {}) {
  return ensureStorageReady().then(() => {
    const instance = ensureWorker();
    const id = `mls-${++nextId}`;

    return new Promise((resolve, reject) => {
      const timeoutMs = (() => {
        const candidate = Number(options?.timeoutMs);
        if (Number.isFinite(candidate) && candidate > 0) {
          return Math.max(250, Math.trunc(candidate));
        }
        if (String(command || '').trim().toLowerCase() === 'init') {
          return INIT_WORKER_CALL_TIMEOUT_MS;
        }
        return DEFAULT_WORKER_CALL_TIMEOUT_MS;
      })();
      const timeoutEntry = { resolve, reject, timeoutId: null };
      pending.set(id, timeoutEntry);

      if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
        timeoutEntry.timeoutId = setTimeout(() => {
          const entry = pending.get(id);
          if (!entry) return;
          pending.delete(id);
          clearPendingEntryTimeout(entry);
          const timeoutError = new Error(
            `MLS worker call timed out (${String(command)} after ${timeoutMs}ms)`,
          );
          timeoutError.code = 'MLS_WORKER_CALL_TIMEOUT';
          timeoutError.command = command;
          timeoutError.timeoutMs = timeoutMs;
          entry.reject(timeoutError);

          if (options?.restartOnTimeout === true) {
            try {
              const previousWorker = worker;
              worker = null;
              if (previousWorker && typeof previousWorker.terminate === 'function') {
                previousWorker.terminate();
              }
            } catch {}

            try {
              const replacement = createWorkerInstance();
              if (replacement) {
                worker = replacement;
                attachWorkerEventListeners(worker);
              } else {
                workerSource = 'stub';
                workerSourceUrl = null;
                worker = new StubWorker();
              }
            } catch {
              workerSource = 'stub';
              workerSourceUrl = null;
              worker = new StubWorker();
            }
            rejectAllPending(createWorkerRestartedError());
          }
        }, timeoutMs);
      }

      const message = { id, command, payload };
      const transferSet = new Set();
      if (payload && typeof payload === 'object') {
        const pushTransfer = (value) => {
          if (!value) return;
          if (value instanceof ArrayBuffer) {
            transferSet.add(value);
            return;
          }
          if (ArrayBuffer.isView(value) && value.buffer) {
            transferSet.add(value.buffer);
          }
        };

        pushTransfer(payload.welcome);
        pushTransfer(payload.ratchetTree);
        pushTransfer(payload.ratchetTreeBytes);
        pushTransfer(payload.ratchet_tree_bytes);
        pushTransfer(payload.ratchetTreeBuffer);
        pushTransfer(payload.ratchet_tree_buffer);
      }

      if (transferSet.size > 0) {
        instance.postMessage(message, Array.from(transferSet));
      } else {
        instance.postMessage(message);
      }
    });
  });
}

export function terminateMlsWorker() {
  if (worker) {
    worker.terminate();
    worker = null;
    rejectAllPending(createWorkerRestartedError());
  }
}
